import type { DeviceType, VersionEnforcement } from '@/lib/shared-types';
import type {
  DeviceHealth,
  DeviceVersionPolicy,
  FleetDevice,
  VersionCompliance,
  VersionVerdict,
} from './fleetApi';

/**
 * ## Health, derived here because the list endpoint does not send it
 *
 * ADR-0009 §6 is emphatic that health is **derived at read time and never
 * stored** — a `health` column goes stale the moment a device stops beating,
 * and reports HEALTHY for a tablet that has been in a drawer since Tuesday.
 * The backend honours that in `DeviceFleetService.diagnose` and
 * `branchHealth`, both of which compute it per request.
 *
 * `GET /devices` does not: `DevicesService.findAll` returns the raw row plus a
 * pairing sentinel, with no verdict attached. So the registry list has to apply
 * the same rule client-side, and this is a deliberate mirror of
 * `src/modules/devices/device-health.ts` in the backend — same thresholds, same
 * precedence, same names. It is reported as a contract gap; if the list later
 * grows a `health` field, delete this and read that instead.
 *
 * The precedence is the part that matters and is easy to get subtly wrong:
 * a revoked device that also never beat is **REVOKED**, because that is the
 * sentence an operator needs. Its silence is the consequence, not the news.
 */

/** Silent this long and the device is late. Mirrors `DEVICE_STALE_AFTER_MS`. */
export const DEVICE_STALE_AFTER_MS = 15 * 60 * 1000;

/** Silent this long and nobody is holding it. Mirrors `DEVICE_OFFLINE_AFTER_MS`. */
export const DEVICE_OFFLINE_AFTER_MS = 60 * 60 * 1000;

export interface DeviceHealthInput {
  isActive: boolean;
  revokedAt: string | null;
  lastSeenAt: string | null;
  /** True while the row still wears the pending-pairing sentinel. */
  awaitingPairing: boolean;
  /** Non-compliance degrades a device that is otherwise perfectly alive. */
  versionCompliant: boolean;
}

export function deriveDeviceHealth(input: DeviceHealthInput, now: number = Date.now()): DeviceHealth {
  if (input.revokedAt || !input.isActive) {
    return input.awaitingPairing ? 'NEVER_SEEN' : 'REVOKED';
  }
  if (input.awaitingPairing || !input.lastSeenAt) return 'NEVER_SEEN';

  const seenAt = new Date(input.lastSeenAt).getTime();
  if (Number.isNaN(seenAt)) return 'NEVER_SEEN';

  const silentFor = now - seenAt;
  if (silentFor >= DEVICE_OFFLINE_AFTER_MS) return 'OFFLINE';
  if (silentFor >= DEVICE_STALE_AFTER_MS) return 'DEGRADED';
  return input.versionCompliant ? 'HEALTHY' : 'DEGRADED';
}

/**
 * Dotted numeric comparison, segment by segment, with a shorter version treated
 * as zero-padded (`2.4` === `2.4.0`).
 *
 * A trailing non-numeric suffix sorts BELOW the release it precedes:
 * `2.4.0-beta` < `2.4.0`. That is the direction that matters — a pre-release
 * build in the field is exactly what a minimum-version floor is trying to
 * catch, and treating it as equal would wave it through.
 *
 * Anything unparseable returns null rather than guessing. A device reporting
 * `nightly` is UNKNOWN, never SUPPORTED.
 */
export function compareVersions(left: string, right: string): number | null {
  const parse = (value: string): { parts: number[]; prerelease: boolean } | null => {
    const match = /^(\d+(?:\.\d+)*)(?:[-+](.+))?$/.exec(value.trim());
    if (!match) return null;
    return { parts: match[1].split('.').map(Number), prerelease: match[2] !== undefined };
  };

  const a = parse(left);
  const b = parse(right);
  if (!a || !b) return null;

  const length = Math.max(a.parts.length, b.parts.length);
  for (let i = 0; i < length; i += 1) {
    const diff = (a.parts[i] ?? 0) - (b.parts[i] ?? 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  if (a.prerelease === b.prerelease) return 0;
  return a.prerelease ? -1 : 1;
}

export interface VersionPolicyInput {
  minSupportedVersion: string;
  recommendedVersion: string | null;
  enforcement: VersionEnforcement;
}

export function evaluateVersion(
  reportedVersion: string | null | undefined,
  policy: VersionPolicyInput | null,
): VersionVerdict {
  const reported = reportedVersion ?? null;
  const base = {
    reportedVersion: reported,
    minSupportedVersion: policy?.minSupportedVersion ?? null,
    recommendedVersion: policy?.recommendedVersion ?? null,
  };
  if (!policy || !reported) return { ...base, compliance: 'UNKNOWN', blocked: false };

  const againstMin = compareVersions(reported, policy.minSupportedVersion);
  if (againstMin === null) return { ...base, compliance: 'UNKNOWN', blocked: false };
  if (againstMin < 0) {
    return { ...base, compliance: 'UNSUPPORTED', blocked: policy.enforcement === 'BLOCK' };
  }

  if (policy.recommendedVersion) {
    const againstRecommended = compareVersions(reported, policy.recommendedVersion);
    if (againstRecommended !== null && againstRecommended < 0) {
      return { ...base, compliance: 'OUTDATED', blocked: false };
    }
  }
  return { ...base, compliance: 'SUPPORTED', blocked: false };
}

/** The policy governing one device type, out of the set loaded for a tenant. */
export function policyFor(
  policies: DeviceVersionPolicy[],
  type: DeviceType,
): VersionPolicyInput | null {
  const found = policies.find((policy) => policy.deviceType === type);
  return found
    ? {
        minSupportedVersion: found.minSupportedVersion,
        recommendedVersion: found.recommendedVersion,
        enforcement: found.enforcement,
      }
    : null;
}

export interface AssessedDevice extends FleetDevice {
  health: DeviceHealth;
  version: VersionVerdict;
  awaitingPairing: boolean;
}

/** The whole verdict for one registry row, in one call. */
export function assessDevice(
  device: FleetDevice,
  policies: DeviceVersionPolicy[],
  now: number = Date.now(),
): AssessedDevice {
  const version = evaluateVersion(device.appVersion, policyFor(policies, device.type));
  // The sentinel is the server's, not ours: `present()` reports it as a pairing
  // status rather than leaking the hashed ticket in `fingerprint`.
  const awaitingPairing = (device.pairing?.status ?? 'PAIRED') !== 'PAIRED';
  const health = deriveDeviceHealth(
    {
      isActive: device.isActive,
      revokedAt: device.revokedAt ?? null,
      lastSeenAt: device.lastSeenAt,
      awaitingPairing,
      versionCompliant: version.compliance !== 'UNSUPPORTED',
    },
    now,
  );
  return { ...device, health, version, awaitingPairing };
}

/** Counts by health, for the rollup strip when the API's own is unavailable. */
export function countByHealth(devices: AssessedDevice[]): Record<DeviceHealth, number> {
  const counts: Record<DeviceHealth, number> = {
    HEALTHY: 0,
    DEGRADED: 0,
    OFFLINE: 0,
    REVOKED: 0,
    NEVER_SEEN: 0,
  };
  for (const device of devices) counts[device.health] += 1;
  return counts;
}

/** The compliance values that mean somebody has an upgrade to do. */
export const NON_COMPLIANT: VersionCompliance[] = ['UNSUPPORTED', 'OUTDATED'];
